<template>
    <section class="product-listings-content">
        <bread-crumbs
            v-if="!hideBreadcrumbs"
            :crumbs="breadcrumbs"
        />

        <div class="header-section">
            <div class="header">
                <h1
                    class="active-category"
                    v-test:activeCategory
                >
                    {{ headingText || activeCategory }}
                </h1>
            </div>

            <collapsible-filter-panel-group
                v-if="filterPanels.length > 0"
                group-id="filter-sort"
                :panels="filterPanels"
                @update-selected-filters="updateSelectedFilters"
            />
        </div>

        <product-listings
            :items="items"
            :stat-to-show="statToShow"
            :columns="columns"
        />

        <p
            v-if="calorieNotice"
            class="calorie-notice"
            v-test:calorieNotice
        >
            {{ calorieNotice }}
        </p>

        <secondary-nav-bar
            v-if="!hideNavItems && navItems.length > 0"
            :menu-items="navItems"
        />

        <read-more
            v-if="readMoreButtonText && readMoreContent"
            :button-text="readMoreButtonText"
            :content="readMoreContent"
        />
    </section>
</template>

<script>
    import ProductListings from '../../molecules/ProductListings/ProductListings';
    import BreadCrumbs from '../../atoms/BreadCrumbs/BreadCrumbs';
    import CollapsibleFilterPanelGroup from '../../molecules/CollapsibleFilterPanelGroup/CollapsibleFilterPanelGroup';
    import SecondaryNavBar from '../../molecules/SecondaryNavBar/SecondaryNavBar';
    import ReadMore from '../../molecules/ReadMore/ReadMore';
    import createPropProxy from '../../../lib/prop-proxies';

    export default {
        name: 'product-listings-content',

        components: { ReadMore, SecondaryNavBar, CollapsibleFilterPanelGroup, BreadCrumbs, ProductListings },

        props: {
            breadcrumbs: {
                type: Array,
                default: () => ([]),
            },

            hideBreadcrumbs: {
                type: Boolean,
                default: false,
            },

            headingText: {
                type: String,
                default: '',
            },

            filterTitle: {
                type: String,
                default: '',
            },

            filters: {
                type: Array,
                default: () => ([]),
            },

            sortTitle: {
                type: String,
                default: '',
            },

            sorts: {
                type: Array,
                default: () => ([]),
            },

            items: {
                type: Array,
                default: () => ([]),
            },

            navItems: {
                type: Array,
                default: () => ([]),
            },

            hideNavItems: {
                type: Boolean,
                default: false,
            },

            readMoreButtonText: {
                type: String,
                default: '',
            },

            readMoreContent: {
                type: String,
                default: '',
            },

            categoryPrefix: {
                type: String,
                default: '',
            },

            selectedFilters: {
                type: Array,
                default: () => ([]),
            },

            selectedSorts: {
                type: Array,
                default: () => ([]),
            },

            columns: {
                type: Number,
                default: 3,
            },
        },

        computed: {
            /**
             * Create selectedFiltersProxy and selectedSortsProxy.
             *
             * @returns {object}
             */
            ...createPropProxy(['selectedFilters', 'selectedSorts']),

            /**
             * Get calorie notice.
             *
             * @returns {string}
             */
            calorieNotice() {
                return this.$snippet('calorie_notice_text', '');
            },

            /**
             * Get the category name the user is currently viewing.
             *
             * @returns {string}
             */
            activeCategory() {
                return [...this.breadcrumbs].pop()?.text;
            },

            /**
             * Get the stat to show.
             *
             * @returns {string}
             */
            statToShow() {
                return this.selectedSortsProxy[0] || 'calories';
            },

            /**
             * Get the filter panels from the filters and sorts.
             *
             * @returns {object[]}
             */
            filterPanels() {
                const panels = [];
                if (this.filters.length > 0) {
                    panels.push({
                        id: 'filterPanel',
                        title: this.filterTitle,
                        type: 'multiple',
                        selectedFilters: this.selectedFiltersProxy,
                        filters: this.filters,
                    });
                }
                if (this.sorts.length > 0) {
                    panels.push({
                        id: 'sortPanel',
                        title: this.sortTitle,
                        type: 'single',
                        selectedFilters: this.selectedSortsProxy,
                        filters: this.sorts,
                    });
                }
                return panels;
            },
        },

        methods: {
            /**
             * Update selected filters.
             *
             * @param {object} val
             * @param {string} id
             */
            updateSelectedFilters(val, id) {
                if (id === 'filterPanel') {
                    this.$emit('update:selectedFilters', val);
                } else if (id === 'sortPanel') {
                    this.$emit('update:selectedSorts', val);
                }
            },
        },
    };
</script>

<style lang="scss" scoped>
    .bread-crumbs {
        display: none;

        @include media-breakpoint-up(lg) {
            display: block;
            margin-bottom: 17px;
        }
    }

    .header-section {
        position: relative;

        @include media-breakpoint-up(md) {
            border-bottom: solid 2px primary-palette(2);
            margin-bottom: 48px;
            display: flex;
            justify-content: space-between;
            align-items: flex-end;
        }
    }

    .collapsible-filter-panel {
        z-index: $zindex-dropdown;
        display: flex;
        justify-content: flex-end;

        ::v-deep .content-container {
            display: flex;
            align-items: center;

            @include media-breakpoint-up(md) {
                position: relative;
            }
        }

        ::v-deep .filter-panel-collapse {
            width: 310px;
        }
    }

    .header {
        font-size: rem(32px);
        font-weight: bold;
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        margin-bottom: 15px;

        @include media-breakpoint-up(md) {
            margin-bottom: 0;
            padding-right: 15px;
            font-size: rem(48px);
            line-height: rem(56px);
            font-weight: 400;
        }
    }

    .active-category {
        font-size: rem(32px);
        line-height: inherit;
        font-weight: bold;
        margin-bottom: 0;
        display: inline;

        @include media-breakpoint-up(md) {
            font-size: rem(40px);
        }

        &:not(:last-child) {
            margin-right: auto;

            @include media-breakpoint-up(md) {
                margin-right: rem(15px);
            }
        }
    }

    .divider {
        padding: 0 0.15em;
    }

    .calorie-notice {
        text-align: center;
        padding: 35px 0 16px;
        font-size: rem(12px);
        color: #bdbdbd;
        margin-bottom: 0;
    }

    .secondary-nav-bar.secondary-nav-bar {
        position: fixed;
        bottom: 0;
        left: 0;
        right: 0;
        z-index: $zindex-fixed - 1;

        @include media-breakpoint-up(md) {
            position: unset;
            background-color: #ffffff;
            margin-bottom: 16px;
        }
    }

    .read-more {
        display: none;

        @include media-breakpoint-up(md) {
            display: block;
            max-width: 645px;
            margin: 0 auto;
        }
    }
</style>
